import { useSiteSettings } from "@/contexts/SettingsContext";
import { Sparkles } from "lucide-react";
import DOMPurify from "dompurify";

const SAFE_HTML_CONFIG = {
  ALLOWED_TAGS: ["strong", "em", "a", "br"],
  ALLOWED_ATTR: ["href", "target", "rel", "class"],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):)/i,
};
const sanitize = (html) => DOMPurify.sanitize(html, SAFE_HTML_CONFIG);

function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split(/\r?\n/);
  const out = [];
  let listBuffer = [];
  const flushList = () => {
    if (listBuffer.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-6 space-y-1.5 text-zinc-700 my-3">
          {listBuffer.map((item, i) => (
            <li key={`li-${out.length}-${i}`} dangerouslySetInnerHTML={{ __html: sanitize(inlineFmt(item)) }} />
          ))}
        </ul>
      );
      listBuffer = [];
    }
  };
  const inlineFmt = (s) =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\[(.+?)\]\((https?:[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-emerald-700 underline">$1</a>')
      .replace(/(https?:\/\/[^\s)]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-emerald-700 underline">$1</a>');

  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) {
      flushList();
      return;
    }
    if (line.startsWith("# ")) {
      flushList();
      out.push(<h1 key={idx} className="font-heading text-3xl sm:text-4xl font-bold text-zinc-950 mt-8 mb-3" dangerouslySetInnerHTML={{ __html: sanitize(inlineFmt(line.slice(2))) }} />);
    } else if (line.startsWith("## ")) {
      flushList();
      out.push(<h2 key={idx} className="font-heading text-xl font-semibold text-zinc-900 mt-8 mb-2" dangerouslySetInnerHTML={{ __html: sanitize(inlineFmt(line.slice(3))) }} />);
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      listBuffer.push(line.slice(2));
    } else {
      flushList();
      out.push(<p key={idx} className="text-zinc-700 leading-relaxed my-2" dangerouslySetInnerHTML={{ __html: sanitize(inlineFmt(line)) }} />);
    }
  });
  flushList();
  return out;
}

const DEFAULT_CONTENT = (appName) => `## Notre mission
${appName} reconnecte les campeurs aux propriétaires de terrains privés partout au Québec. Nous croyons que chaque coin de nature mérite d'être partagé — et que chaque propriétaire mérite d'en tirer un revenu.

## Pourquoi ${appName} ?
- **Authentique** : des terrains uniques, hors des sentiers battus.
- **Sécuritaire** : hôtes vérifiés, paiements sécurisés via Stripe.
- **Local** : 100% québécois, propulsé par des passionnés de plein air.

## Une question ?
Écrivez-nous et nous reviendrons rapidement vers vous.`;

export default function AboutPage() {
  const { settings } = useSiteSettings();
  const appName = settings?.app_name || "VroumZy";
  const title = (settings?.about_title || "").trim() || `À propos de ${appName}`;
  const subtitle = (settings?.about_subtitle || "").trim() || "L'histoire derrière la plateforme.";
  const content = (settings?.about_content && settings.about_content.trim()) || DEFAULT_CONTENT(appName);
  const image = (settings?.about_image || "").trim();

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      {/* Hero */}
      <header className="border-b border-zinc-200 bg-gradient-to-br from-emerald-50/60 via-white to-white">
        <div className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100/70 text-emerald-800 text-xs font-semibold mb-4">
            <Sparkles className="h-3.5 w-3.5" /> À propos
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-zinc-950 leading-tight" data-testid="about-title">
            {title}
          </h1>
          <p className="text-lg text-zinc-600 mt-3 max-w-2xl" data-testid="about-subtitle">{subtitle}</p>
        </div>
      </header>

      {/* Main image (only when configured) */}
      {image && (
        <div className="max-w-4xl mx-auto px-4 -mt-6 sm:-mt-8">
          <div className="rounded-2xl overflow-hidden shadow-md ring-1 ring-zinc-200 bg-white" data-testid="about-image-wrap">
            <img src={image} alt={title} className="w-full h-64 sm:h-96 object-cover" data-testid="about-image" />
          </div>
        </div>
      )}

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-10 sm:py-14">
        <article className="prose prose-zinc max-w-none" data-testid="about-content">
          {renderMarkdown(content)}
        </article>
      </main>
    </div>
  );
}
